import { useInfiniteQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import ms from 'ms';

import { toast } from '@hivebrite/design-system/Toast';

import { COMMENTS_PER_PAGE } from 'hb-react/frontoffice/apps/CommentsV2/constants';
import type {
  CommentsQueryData,
  GetCommentsResponse,
  PageParamType,
} from 'hb-react/frontoffice/apps/CommentsV2/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { configToSnakeCase } from 'hb-react/shared/utils/axiosInterceptors';

export const useComments = ({
  queryKey,
  params,
  enabled = true,
}: {
  queryKey: Array<unknown>;
  params: {
    commentableId: number;
    commentableType: string;
    focusedCommentId?: number;
    order?: 'asc' | 'desc';
    limit?: number;
    sortBy?: string;
  };
  enabled?: boolean;
}) => {
  const { currentUser } = useDefaultAppContexts();
  const userId = currentUser?.id as number;
  const { limit = COMMENTS_PER_PAGE, focusedCommentId } = params;

  return useInfiniteQuery<
    GetCommentsResponse,
    AxiosError,
    CommentsQueryData,
    Array<unknown>,
    PageParamType
  >({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const url = Routes.user_comments_path(userId);

      try {
        const response = await axios.get<GetCommentsResponse>(
          url,
          configToSnakeCase({
            params: {
              ...params,
              limit,
              ...pageParam,
            },
            signal,
          }),
        );

        return response.data;
      } catch (error) {
        toast.error(__('Sorry, an error occurred'));
        throw error;
      }
    },
    enabled,
    // Focused Comment ID is stored in page params, so it can be used during re-fetching after invalidation.
    initialPageParam: { focusedCommentId },
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page
        ? { afterCursor: lastPage.end_cursor, focusedCommentId: undefined }
        : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.has_previous_page
        ? { beforeCursor: firstPage.start_cursor, focusedCommentId: undefined }
        : undefined,
    staleTime: ms('5m'),
  });
};
